import { useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import SmartLoader from '@/components/ui/SmartLoader';

/**
 * Wrapper simple que maneja toda la lógica de auth y empresa
 * Reemplaza SafeEmpresaProvider + EmpresaMiddleware + AuthMiddleware
 */
export const SimpleAuthWrapper = ({ children }) => {
  const { user, isLoaded } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    loading, 
    inicializado, 
    empresaActual, 
    empresasDisponibles, 
    needsOnboarding,
    hasCurrentEmpresa 
  } = useSimpleEmpresa();

  // Rutas públicas que no necesitan auth
  const rutasPublicas = ['/taller', '/cliente', '/landing'];
  const esRutaPublica = rutasPublicas.some(ruta => location.pathname.startsWith(ruta));
  
  // Rutas de onboarding
  const esRutaOnboarding = location.pathname.includes('/onboarding') || 
                          location.pathname === '/select-user-type' || 
                          location.pathname === '/solicitar-acceso' ||
                          location.pathname === '/unauthorized';

  useEffect(() => {
    console.log('🔍 SimpleAuthWrapper effect:', {
      isLoaded,
      user: !!user,
      inicializado,
      needsOnboarding,
      empresasDisponibles: empresasDisponibles.length,
      empresaActual: !!empresaActual,
      hasCurrentEmpresa,
      loading,
      esRutaPublica,
      esRutaOnboarding,
      pathname: location.pathname
    });

    // No hacer nada si no está cargado o es ruta pública
    if (!isLoaded || esRutaPublica) return;

    // Sin usuario - redirigir a landing
    if (!user) {
      console.log('🚫 No hay usuario, redirigiendo a /taller');
      navigate('/taller');
      return;
    }

    // Con usuario pero sin inicializar - esperar
    if (!inicializado) {
      console.log('⏳ Esperando inicialización...');
      return;
    }

    // Usuario inicializado pero necesita onboarding
    if (needsOnboarding && !esRutaOnboarding) {
      console.log('📝 Usuario necesita onboarding');
      const userType = localStorage.getItem('revvly_user_type');
      if (userType === 'taller') {
        navigate('/onboarding/empresa');
      } else if (userType === 'cliente') {
        navigate('/solicitar-acceso');
      } else {
        navigate('/select-user-type');
      }
      return;
    }

    // Usuario con empresas pero sin empresa actual seleccionada
    if (empresasDisponibles.length > 0 && !empresaActual && !esRutaOnboarding) {
      console.log('🏢 Hay empresas disponibles pero ninguna seleccionada');
      // Esto normalmente no debería pasar, pero por seguridad
      if (empresasDisponibles.length === 1) {
        // Auto-seleccionar si solo hay una empresa
        // Se manejará automáticamente en el hook
      } else {
        navigate('/select-empresa');
      }
      return;
    }

    // Todo ok - usuario tiene empresa y está en ruta raíz
    if (hasCurrentEmpresa && location.pathname === '/') {
      console.log('✅ Usuario con empresa en ruta raíz, redirigiendo a dashboard');
      navigate('/dashboard', { replace: true });
    }

  }, [
    isLoaded, 
    user, 
    inicializado, 
    needsOnboarding, 
    empresaActual, 
    empresasDisponibles, 
    hasCurrentEmpresa,
    location.pathname,
    navigate,
    esRutaPublica,
    esRutaOnboarding
  ]);

  // Loading mientras carga Clerk
  if (!isLoaded) {
    return (
      <SmartLoader 
        isVisible={true}
        message="Cargando aplicación..."
        subtitle="Inicializando sesión"
      />
    );
  }

  // Loading mientras carga empresas (solo si hay usuario y no es ruta pública/onboarding)
  if (user && !esRutaPublica && !esRutaOnboarding && loading && !inicializado) {
    return (
      <SmartLoader 
        isVisible={true}
        message="Configurando workspace..."
        subtitle="Cargando información de empresa"
      />
    );
  }

  // Renderizar children
  return children;
};

export default SimpleAuthWrapper;